import "@/bootstrap";
import "cropperjs/dist/cropper.css";
import "../css/app.css";
import "../css/custom-filepond.css";
import "../css/tooltip.scss";

// import DesignSystem from "@/Layouts/PrimeVue";
import {
  AXIOS_NETWORK_ERROR_COOLDOWN_MS,
  AXIOS_NETWORK_ERROR_EVENT,
  buildNetworkErrorNotification,
  shouldNotifyNetworkError,
  useNotification,
} from "@/Library";
import { bugsnag, echo, pinia } from "@/Plugins";
import { VanillaComponents, defineConfiguration } from "@flavorly/vanilla-components";
import { createInertiaApp } from "@inertiajs/vue3";
import { ModuleRegistry, themeQuartz } from "ag-grid-community";
import { AllEnterpriseModule, LicenseManager } from "ag-grid-enterprise";
import { AgGridVue } from "ag-grid-vue3";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";
// import PrimeVue from "primevue/config";
import { router } from "@inertiajs/vue3";
import { createApp, h } from "vue";
import { ZiggyVue } from "ziggy-js";
import directives from "./Directives";

ModuleRegistry.registerModules([AllEnterpriseModule]);
LicenseManager.setLicenseKey(
  "Using_this_{AG_Grid}_Enterprise_key_{AG-083821}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Mid-States}_is_granted_a_{Multiple_Applications}_Developer_License_for_{2}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{2_August_2026}____[v3]_[01]_MTc4NTYyNTIwMDAwMA==c2b6642d216300c572b8c298aa7011f4"
);

// Configure the theme
const msdTheme = themeQuartz.withParams({
  fontFamily: "InterVariable",
  accentColor: "#fb2c36",
  borderColor: "#E2E8F0",
  browserColorScheme: "inherit",
  foregroundColor: "#0f172b",
  rowHoverColor: "#f8fafc",
  headerBackgroundColor: "#f8fafc",
  menuBackgroundColor: "#f8fafc",
  headerFontSize: 14,
  headerFontWeight: 600,
  iconSize: 16,
  wrapperBorder: false,
  wrapperBorderRadius: 0,
  backgroundColor: "#fff",
});

const appName = window.document.getElementsByTagName("title")[0]?.innerText || "MidStates";

import moment from "moment-timezone";

moment.tz.setDefault("America/Chicago");

const { notify } = useNotification();
let lastNetworkErrorNotificationAt = 0;

if (typeof window !== "undefined") {
  window.addEventListener(AXIOS_NETWORK_ERROR_EVENT, (event) => {
    const now = Date.now();

    if (!shouldNotifyNetworkError(lastNetworkErrorNotificationAt, now, AXIOS_NETWORK_ERROR_COOLDOWN_MS)) {
      return;
    }

    lastNetworkErrorNotificationAt = now;
    notify(event.detail ?? buildNetworkErrorNotification());
  });
}

createInertiaApp({
  title: (title) => `${title} - ${appName}`,
  resolve: async (name) => {
    const page = await resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob("./Pages/**/*.vue"));
    const AuthenticatedLayout = (await import("@/Layouts/AuthenticatedLayout.vue")).default;
    // Pinia's user store is only populated once AuthenticatedLayout mounts, so on a
    // hard/full page reload it is still empty the first time resolve() runs here.
    // Fall back to the initial page payload Inertia embeds in #app's data-page
    // attribute, which is available synchronously before Vue/Pinia boot.
    const initialPageUser = JSON.parse(document.getElementById("app")?.dataset.page ?? "{}")?.props?.auth?.user;
    const userType = pinia.state.value.user?.type ?? initialPageUser?.type ?? null;

    if (name.startsWith("Auth/")) {
      page.default.layout = (await import("@/Layouts/GuestLayout.vue")).default;
    } else if (name.startsWith("Events/Event/Registration/Steps/One")) {
      const OneLayout = (await import("@/Layouts/Registration/Steps/OneLayout.vue")).default;
      page.default.layout = (h, page) => h(AuthenticatedLayout, () => h(OneLayout, () => page));
    } else if (name.startsWith("Events/Event/Registration/Steps/Two")) {
      if (name.endsWith("GeneratePDF")) {
        page.default.layout = null;
      } else {
        const TwoLayout = (await import("@/Layouts/Registration/Steps/TwoLayout.vue")).default;
        page.default.layout = (h, page) => h(AuthenticatedLayout, () => h(TwoLayout, () => page));
      }
    } else if (name.startsWith("Events/Event/Registration/Steps/Three")) {
      const ThreeLayout = (await import("@/Layouts/Registration/Steps/ThreeLayout.vue")).default;
      page.default.layout = (h, page) => h(AuthenticatedLayout, () => h(ThreeLayout, () => page));
    } else if (name.startsWith("Events/Event/Booth/")) {
      // Suppliers and Reps
      const BoothLayout = (await import("@/Layouts/BoothLayout.vue")).default;
      page.default.layout = (h, page) => h(AuthenticatedLayout, () => h(BoothLayout, () => page));
    } else if (name.startsWith("Events/Event/Show/")) {
      // Members
      const ShowLayout = (await import("@/Layouts/ShowLayout.vue")).default;
      page.default.layout = (h, page) => h(AuthenticatedLayout, () => h(ShowLayout, () => page));
    } else if (
      name.startsWith("Events/Event/") &&
      userType !== null && // can be null on initial load
      !["rep", "vendor"].includes(userType) &&
      !name.startsWith("Events/Event/Summit/")
    ) {
      const EventsLayout = (await import("@/Layouts/EventsLayout.vue")).default;
      page.default.layout = (h, page) => h(AuthenticatedLayout, () => h(EventsLayout, () => page));
    } else {
      page.default.layout = page.default.layout || AuthenticatedLayout;
    }

    return page;
  },
  setup({ el, App, props, plugin }) {
    const app2 = createApp({ render: () => h(App, props) });
    app2
      .use(plugin)
      .use(ZiggyVue)
      .use(pinia)
      .use(echo)
      .use(bugsnag)
      .use(
        VanillaComponents,
        defineConfiguration({
          RichSelect: {
            classes: {
              trigger:
                "bg-white dark:bg-slate-900 border-0 ring-1 ring-inset focus:ring-inset focus:ring-2 ring-slate-300 focus:ring-primary-600 dark:ring-white/20 dark:focus:ring-primary-600 focus-visible:ring-primary-600 relative flex items-center justify-between w-full focus:z-10 cursor-pointer sm:text-sm transition duration-100 ease-in-out shadow-xs disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-md",
              dropdown:
                "z-50 min-w-max overflow-hidden bg-white dark:bg-slate-900 border border-1 border-slate-300 dark:border-slate-700 rounded-md shadow-lg",
              selectButtonPlaceholder: "block text-slate-500/90 truncate",
              searchInput:
                "form-input inline-block w-full shadow-inner focus:ring-opacity-70 text-sm px-4 py-2.5 text-slate-700 dark:text-white placeholder-slate-500/60 ring-1 ring-inset focus:ring-inset focus:ring-2 ring-slate-300 focus:ring-primary-600 dark:ring-white/20 dark:focus:ring-primary-600 focus-visible:ring-primary-600 border-0 rounded-lg shadow bg-slate-50 dark:bg-slate-800",
            },
          },
          Input: {
            fixedClasses: {
              input:
                "text-sm appearance-none w-full focus:outline-hidden shadow-xs disabled:opacity-50 disabled:cursor-not-allowed border-0 rounded-md",
            },
            classes: {
              input:
                "border-0 text-slate-700 dark:text-white placeholder-slate-500/60 bg-white dark:bg-slate-900 ring-1 ring-inset focus:ring-inset focus:ring-2 ring-slate-300 focus:ring-primary-600 dark:ring-white/20 dark:focus:ring-primary-600 focus-visible:ring-primary-600 px-4 py-2 !autofill:bg-white !dark:autofill:bg-slate-900",
            },
          },
          Button: {
            classes: {
              button:
                "text-sm font-semibold whitespace-nowrap focus-visible:outline focus-visible:outline-2 shadow-xs focus-visible:outline-offset-2 focus-visible:outline-primary-500 text-slate-700 focus-visible:text-slate-600 dark:text-white dark:hover:text-white dark:focus:text-white bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 dark:focus:border-primary-600",
            },
            variants: {
              primary: {
                classes: {
                  button:
                    "text-sm font-semibold whitespace-nowrap focus-visible:outline shadow-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 text-white focus:text-white bg-primary-500 hover:bg-primary-600",
                },
              },
            },
          },
          TagsInput: {
            classes: {
              tag: "rounded-sm shadow-xs flex items-center cursor-pointer bg-primary-100 m-0.5 h-6 max-w-full text-primary-700 white-space-no text-sm font-medium focus:outline-hidden focus:ring-2 focus:ring-opacity-50 focus:ring-primary-500 transition duration-100 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed",
              trigger:
                "bg-white border-0 ring-1 ring-inset focus:ring-inset focus:ring-2 ring-slate-300 focus:ring-primary-600 focus-visible:ring-primary-600 text-sm transition duration-100 ease-in-out shadow-xs disabled:opacity-50 disabled:cursor-not-allowed rounded-md px-2 py-2.5 relative flex items-center w-full focus:z-10 cursor-pointer max-w-full w-full",
            },
          },
        })
      )
      // .use(PrimeVue, {
      //   unstyled: true,
      //   pt: DesignSystem,
      // })
      .component("AgGridVue", AgGridVue)
      .provide("msdTheme", msdTheme);
    directives(app2);

    app2.mount(el);

    // We are deciding to not use local storage to store the state of the store
    // initializeStoreSync();
  },
  progress: {
    color: "oklch(0.637 0.237 25.331)",
  },
  onError: (errors) => {
    // Handle session timeout (419 CSRF token mismatch)
    if (errors?.response?.status === 419) {
      window.location.href = "/login";
    }
  },
});

// Additional error handler for router errors (navigation, form submissions)
router.on("error", (event) => {
  if (event.detail.response?.status === 419) {
    window.location.href = "/login";
  }
});

// Session timeout watcher - redirects BEFORE 401 errors occur
// Resets ONLY on server requests (Inertia navigation) or axios requests, not on client-side navigation that doesn't hit the server
let sessionTimeoutId = null;

const startSessionTimeout = (sessionLifetime) => {
  if (!sessionLifetime || sessionLifetime <= 0) return;

  // Clear any existing timeout
  if (sessionTimeoutId) {
    clearTimeout(sessionTimeoutId);
  }

  // Convert minutes to milliseconds
  const timeoutMs = sessionLifetime * 60 * 1000 + 30 * 1000; // Add 30 seconds buffer to ensure we redirect before session expires

  // Set timeout to redirect before session expires
  sessionTimeoutId = setTimeout(() => {
    console.log("Session timeout detected - redirecting to login");
    window.location.href = "/login";
  }, timeoutMs);
};

// Reset timeout on every Inertia navigation (which makes a server request and extends session)
router.on("navigate", (event) => {
  const sessionLifetime = event.detail.page.props.session_lifetime;
  const isAuthenticated = event.detail.page.props.auth?.user;

  if (isAuthenticated && sessionLifetime) {
    startSessionTimeout(sessionLifetime);
  }
});

// Also reset timeout on axios requests (which also extend PHP session)
window.axios.interceptors.response.use(
  (response) => {
    // Get session lifetime from Inertia page props if available
    const sessionLifetime = window?.$page?.props?.session_lifetime;
    if (sessionLifetime) {
      startSessionTimeout(sessionLifetime);
    }
    return response;
  },
  (error) => {
    // Error handling is already done in bootstrap.js
    return Promise.reject(error);
  }
);
